<script setup>
import { ref, computed, inject, onMounted } from 'vue';
import * as navConfig from '@/navConfig';
import { router } from '@inertiajs/vue3';
import { useDisplay } from 'vuetify';
import { useUserStore } from '@/stores/user';
import { useOptionsStore } from '@/stores/options';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';

defineOptions({
    inheritAttrs: false
});

const drawer = ref(true);
const rail = ref(false);
const { thresholds } = useDisplay();

const { profile, lastLogin } = storeToRefs(useUserStore());
const { securityCheckpoint } = storeToRefs(useOptionsStore());
const roles = computed(() => isRole(profile.value));

const isLoading = inject('isLoading');

const routes = computed(() => {
    let allRoutes = [];
    if (roles.value.superAdmin()) {
        allRoutes.push(...navConfig.superAdmin());
    }

    if (roles.value.admin()) {
        allRoutes.push(...navConfig.admin());
    }

    // Include agent-specific navigation (previously missing, causing empty menu for agents)
    if (roles.value.agent()) {
        allRoutes.push(...navConfig.agent());
    }

    if (roles.value.user()) {
        allRoutes.push(...navConfig.user());
    }

    const uniqueRoutes = allRoutes.reduce((acc, current) => {
        const x = acc.find(item => item.name === current.name);
        return x ? acc : [...acc, current];
    }, []);

    return uniqueRoutes;
});

const bottomNavRoutes = computed(() => {
    if (roles.value.superAdmin()) {
        return navConfig.bottomNavRoutes();
    } else {
        return navConfig.bottomNavRoutes().filter(x => !x.private);
    }
});

const isLocal = computed(() => {
    return import.meta.env.VITE_ENV === 'local';
});

onMounted(() => {
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
});

const logout = async () => {
    await router.post('/logout');
};

const changeBar = () => {
    if (window.innerWidth <= thresholds.value.md) {
        rail.value = false;
    }
    drawer.value = !drawer.value;
};

const activeRoute = ref(null);
const showSubscriptionModal = ref(false);

const visitPath = (route) => {
    if (route.path === '/widgets' && profile.value && profile.value.has_valid_subscription === false) {
        showSubscriptionModal.value = true;
        return;
    }
    activeRoute.value = route.path;
    if (route.private) {
        window.open(route.path, '_blank');
    } else {
        router.visit(route.path);
    }
    if (window.innerWidth <= thresholds.value.md) {
        drawer.value = false;
    }
};

</script>

<template>
<div>
    <v-app>
        <div
            v-if="$page.props.impersonation?.active"
            class="btn text-white text-sm md:text-base px-3 py-1 flex items-center justify-between fixed top-0 left-0 w-full z-[1100] shadow-lg max-h-[30px]">
            <span>{{ $t('auth.impersonating', { name: (profile?.first_name || '') + ' ' + (profile?.last_name || '') }) }}</span>
            <v-btn
                size="x-small"
                variant="flat"
                color="white"
                class="text-amber-700 font-semibold"
                @click.prevent="router.post('/api/v1/agent/impersonate/leave', {}, { onSuccess: () => window.location.reload() })">
                {{ $t('auth.exit_impersonation') }}
            </v-btn>
        </div>
        <v-navigation-drawer
            v-if="!securityCheckpoint"
            v-model="drawer"
            :rail="rail"
            mobile-breakpoint="md"
            app>
            <v-list>
                <img
                    :src="rail ? '/images/icon-logo.png' : '/images/logo.png'"
                    :class="rail ? 'mx-auto px-2 pt-1.5' : 'w-full px-4 object-contain h-14'"
                    alt="Logo"
                    class="cursor-pointer"
                    @click="$inertia.visit('/offers')">
            </v-list>

            <v-list class="nav">
                <div class="hidden md:flex" :class="!rail ? 'justify-end' : 'justify-center'">
                    <v-app-bar-nav-icon
                        v-tooltip.right="rail ? 'Open' : 'Minimize'"
                        color="accent"
                        :icon="!rail ? 'mdi-arrow-collapse-left' : 'mdi-arrow-collapse-right'"
                        @click="rail = !rail" />
                </div>
                <v-list-item
                    v-for="route in routes"
                    :key="route.name"
                    v-tooltip.right="!drawer ? '' : route.label"
                    :class="{'v-list-item--active': route.name === $page.component}"
                    :variant="route.name === $page.component ? 'tonal' : 'text'"
                    :title="route.label"
                    :value="route.name"
                    @click="visitPath(route)">
                    <template #prepend>
                        <v-progress-circular
                            v-if="isLoading && route.path === activeRoute"
                            :width="3"
                            :size="24"
                            class="mr-8"
                            color="secondary"
                            indeterminate />
                        <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                        <v-icon v-else :icon="route.icon" />
                    </template>
                </v-list-item>
            </v-list>

            <template v-if="!securityCheckpoint" #append>
                <div class="border-t border-grey-500">
                    <v-list>
                        <v-list-item
                            v-for="route in bottomNavRoutes"
                            :key="route.name"
                            v-tooltip.right="!drawer ? '' : route.label"
                            :class="{'v-list-item--active': route.name === $page.component}"
                            :variant="route.name === $page.component ? 'tonal' : 'text'"
                            :title="route.label"
                            :value="route.name"
                            @click="visitPath(route)">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && route.path === activeRoute"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="activeRoute === route.path" :icon="route.icon" color="secondary" />
                                <v-icon v-else :icon="route.icon" />
                            </template>
                        </v-list-item>

                        <v-list-item
                            v-if="isLocal"
                            v-tooltip.right="!drawer ? '' : 'Project Setup'"
                            prepend-icon="mdi-magnify"
                            title="Project Setup"
                            @click.prevent="$inertia.visit('/local/project')">
                            <template #prepend>
                                <v-progress-circular
                                    v-if="isLoading && activeRoute === '/local/project'"
                                    :width="3"
                                    :size="24"
                                    class="mr-8"
                                    color="secondary"
                                    indeterminate />
                                <v-icon v-else-if="$page.component === 'Local/ProjectSetup'" icon="mdi-magnify" color="secondary" />
                                <v-icon v-else icon="mdi-magnify" />
                            </template>
                        </v-list-item>
                        <v-list-item
                            v-tooltip.right="!drawer ? '' : 'Sign Out'"
                            prepend-icon="mdi-power-standby"
                            title="Sign Out"
                            value="logout"
                            @click.prevent="logout">
                            <template #prepend>
                                <v-icon icon="mdi-power-standby" />
                            </template>
                        </v-list-item>
                    </v-list>
                </div>
            </template>
        </v-navigation-drawer>

        <v-app-bar v-if="!securityCheckpoint" app>
            <div class="block md:hidden ml-4">
                <v-app-bar-nav-icon @click="changeBar" />
            </div>
            <div class="flex justify-items-end justify-end w-full mr-6">
                <img
                    class="md:hidden block h-12 object-scale-down"
                    src="/images/logo.png"
                    alt="Logo">
            </div>
        </v-app-bar>

        <v-main app>
            <v-container fluid>
                <slot />
            </v-container>
            <!-- Subscription Modal -->
            <v-dialog v-model="showSubscriptionModal" max-width="400">
                <v-card>
                    <v-card-title class="text-h6">{{ $t('label.subscription_required') }}</v-card-title>
                    <v-card-text>
                        {{ $t('label.required_message') }}
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="primary" @click="showSubscriptionModal = false">{{ $t('common.button.ok') }}</v-btn>
                        <v-btn color="secondary" @click="router.visit('/profile'); showSubscriptionModal = false">{{ $t('label.go_to_profile') }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-main>

        <div class="z-[1008]">
            <modals-container />
        </div>

        <v-footer app>
            <div class="mb-0 flex justify-between w-full items-center mx-4 text-sm">
                <div class="italic">{{ $t('auth.last-login', { 'lastLogin': lastLogin }) }}</div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div>Copyright &copy; Fabled Solutions {{ new Date().getFullYear() }}</div>
            </div>
        </v-footer>
    </v-app>
</div>
</template>
